import { Suspense, useEffect, type ComponentType } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LoadingState, PageTransition } from "../components/UIComponents";
import { APP_SCROLL_CONTAINER_ID } from "../constants/layout";
import { useIframeAutoResize } from "../hooks/useIframeAutoResize";
import { routes } from "./routes";

const RouteWithResizeReporter = ({
  path,
  RouteElement,
}: {
  path: string;
  RouteElement: ComponentType;
}) => {
  /*
   * This component sits inside the Routes subtree under AppRouter's Suspense
   * boundary. Its effect cannot run while the lazy route is showing the loading
   * fallback, so Loop Hubs only receives the complete route height rather than
   * an initial viewport height.
   *
   * The bottom inset is deliberately not passed in. The hook reads the applied
   * value from the DOM, and feeding it a changing prop restarted the effect on
   * every host scroll, cancelling measurements mid-flight for no gain.
   */
  useIframeAutoResize({
    heightPadding: 1,
  });

  return (
    <PageTransition key={path}>
      <RouteElement />
    </PageTransition>
  );
};

// Helper function to render routes recursively
const renderRoutes = (routeConfigs: typeof routes) => {
  return routeConfigs.map(route => {
    const RouteElement = route.element;

    return (
      <Route
        key={route.path}
        path={route.path}
        element={
          <RouteWithResizeReporter
            path={route.path}
            RouteElement={RouteElement}
          />
        }
      >
        {route.children && renderRoutes(route.children)}
      </Route>
    );
  });
};

/** Reset page scroll only when navigation changes the route. */
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    const appShell = document.getElementById(APP_SCROLL_CONTAINER_ID);
    if (
      appShell?.scrollHeight &&
      appShell.scrollHeight > appShell.clientHeight
    ) {
      appShell.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname]);
  return null;
};

const AppRouter = () => {
  return (
    <>
      <ScrollToTop />
      <Suspense
        fallback={
          <LoadingState placement="viewport" label="A preparar aplicação…" />
        }
      >
        <Routes>
          {renderRoutes(routes)}
          {/* Catch all route - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRouter;
